import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import CaseForm from '../../components/CaseForm';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

interface Case {
  id: number;
  case_name: string;
  case_type: string;
  service_type_id: number;
  service_type?: { name: string };
  due_date: string;
  status:
    | 'Created'
    | 'Prospecting'
    | 'Approved'
    | 'Retainer_Received'
    | 'In_Person_Requested'
    | 'Virtual_Meeting_Requested'
    | 'Meeting_Scheduled'
    | 'Evaluation_Completed'
    | 'Rejected';
  rejection_comment?: string;
  firm?: { name: string };
  creator?: { name: string };
}

function hasRole(user: any, roleName: string) {
  return user?.roles?.some((r: any) => r.name === roleName);
}

const Cases: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [actionLoading, setActionLoading] = useState<{
    [key: number]: boolean;
  }>({});
  const [showCreate, setShowCreate] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [selectedRejectionComment, setSelectedRejectionComment] = useState('');

  const isAdmin = hasRole(user, 'admin');
  const isFirmAdmin = hasRole(user, 'firm_admin');
  const isExpert = hasRole(user, 'expert_user');
  // console.log("isExpert", isExpert)
  const fetchCases = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/cases');
      console.log('Cases data received:', res.data);
      console.log('First case serviceType:', res.data[0]?.serviceType);
      setCases(res.data);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('You do not have permission to view cases');
      } else {
        setError('Failed to load cases');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && (isAdmin || isFirmAdmin || isExpert)) {
      fetchCases();
    }
  }, [authLoading, isAdmin, isFirmAdmin, isExpert]);

  const handleApprove = async (id: number) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await api.patch(`/cases/${id}/approve`);
      toast.success('Case approved successfully!');
      fetchCases();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to approve case');
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleReject = async () => {
    if (!rejectingId) return;
    setActionLoading(prev => ({ ...prev, [rejectingId]: true }));
    try {
      await api.patch(`/cases/${rejectingId}/reject`, {
        rejection_comment: rejectComment,
      });
      toast.success('Case rejected successfully!');
      setRejectingId(null);
      setRejectComment('');
      fetchCases();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to reject case');
    } finally {
      setActionLoading(prev => ({ ...prev, [rejectingId]: false }));
    }
  };

  const handleProspect = async (id: number) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await api.patch(`/cases/${id}/prospect`);
      toast.success('Case set to prospecting status successfully!');
      fetchCases();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          'Failed to set case to prospecting status'
      );
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleRetainerReceived = async (id: number) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await api.patch(`/cases/${id}/retainer-received`);
      toast.success('Retainer marked as received successfully!');
      fetchCases();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || 'Failed to mark retainer as received'
      );
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleShowRejectionReason = (rejectionComment: string) => {
    setSelectedRejectionComment(rejectionComment);
    setShowRejectionModal(true);
  };

  const getStatusChip = (status: string, rejectionComment?: string) => {
    switch (status) {
      case 'Created':
        return <Chip label='Created' color='warning' onClick={() => false} />;
      case 'Prospecting':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'info.main',
              color: 'info.contrastText',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Prospecting
          </Box>
        );
      case 'Approved':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'success.main',
              color: 'success.contrastText',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Approved
          </Box>
        );
      case 'Retainer_Received':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'purple',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Retainer Received
          </Box>
        );
      case 'In_Person_Requested':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'orange',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            In-Person Requested
          </Box>
        );
      case 'Virtual_Meeting_Requested':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'grey',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Virtual Meeting Requested
          </Box>
        );
      case 'Meeting_Scheduled':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'brown',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Meeting Scheduled
          </Box>
        );
      case 'Evaluation_Completed':
        return (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '16px',
              backgroundColor: 'success.main',
              color: 'success.contrastText',
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Evaluation Completed
          </Box>
        );
      case 'Rejected':
        return rejectionComment ? (
          <Chip
            label='Rejected'
            color='error'
            onClick={e => {
              e.stopPropagation();
              handleShowRejectionReason(rejectionComment);
            }}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <Chip label='Rejected' color='error'  onClick={() => false} />
        );
      default:
        return <Chip label={status}   onClick={() => false} />;
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error if user doesn't have permission
  if (!isAdmin && !isFirmAdmin && !isExpert) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant='h4' gutterBottom>
          Cases
        </Typography>
        <Typography color='error'>
          You do not have permission to view cases.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h4' gutterBottom>
        Cases {isFirmAdmin && `- ${user?.firm?.name || 'Your Firm'}`}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button variant='contained' onClick={() => setShowCreate(true)}>
          Create Case
        </Button>
        {(isAdmin || isExpert) && (
          <Button
            variant='outlined'
            onClick={() => navigate('/admin/cases-with-slots')}
          >
            View Cases with Slots
          </Button>
        )}
      </Box>

      {/* Info for expert users about slot management restrictions */}
      {isExpert && !isAdmin && (
        <Alert severity='info' sx={{ mb: 3 }}>
          <Typography variant='body2'>
            <strong>Slot Management Access:</strong> As an expert, you can only
            access slot management for cases where:
          </Typography>
          <Typography variant='body2' sx={{ mt: 1 }}>
            • You are the assigned expert for the case
          </Typography>
        </Alert>
      )}

      <Dialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Create New Case</DialogTitle>
        <DialogContent>
          <CaseForm
            onSuccess={() => {
              setShowCreate(false);
              fetchCases();
              toast.success('Case created successfully!');
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {error ? (
        <Typography color='error'>{error}</Typography>
      ) : (
        <Card>
          <CardHeader title='All Cases' />
          <CardContent>
            {cases.length === 0 ? (
              <Typography variant='body1' color='text.secondary' align='center'>
                No cases found.
              </Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Case Name</TableCell>
                      <TableCell>Case Type</TableCell>
                      <TableCell>Service Type</TableCell>
                      <TableCell>Status</TableCell>
                      {isAdmin && <TableCell>Firm</TableCell>}
                      <TableCell>Created By</TableCell>
                      {isAdmin && <TableCell>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cases.map(c => (
                      <TableRow key={c.id}>
                        <TableCell>
                          <Typography
                            variant='body2'
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                            onClick={() => navigate(`/admin/cases/${c.id}`)}
                          >
                            {c.case_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{c.case_type}</TableCell>
                        <TableCell>{c.service_type?.name || '-'}</TableCell>
                        <TableCell>
                          {getStatusChip(c.status, c.rejection_comment)}
                        </TableCell>
                        {isAdmin && (
                          <TableCell>{c.firm?.name || '-'}</TableCell>
                        )}
                        <TableCell>{c.creator?.name || '-'}</TableCell>
                        {isAdmin && (
                          <TableCell>
                            {c.status === 'Created' && (
                              <Box display='flex' gap={1}>
                                <IconButton
                                  color='success'
                                  onClick={() => handleApprove(c.id)}
                                  disabled={actionLoading[c.id]}
                                  title='Approve Case'
                                >
                                  {actionLoading[c.id] ? (
                                    <CircularProgress
                                      size={20}
                                      color='success'
                                    />
                                  ) : (
                                    <CheckIcon />
                                  )}
                                </IconButton>
                                <IconButton
                                  color='info'
                                  onClick={() => handleProspect(c.id)}
                                  disabled={actionLoading[c.id]}
                                  title='Set to Prospecting'
                                >
                                  {actionLoading[c.id] ? (
                                    <CircularProgress size={20} color='info' />
                                  ) : (
                                    <InfoIcon />
                                  )}
                                </IconButton>
                                <IconButton
                                  color='error'
                                  onClick={() => setRejectingId(c.id)}
                                  disabled={actionLoading[c.id]}
                                  title='Reject Case'
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Box>
                            )}
                            {c.status === 'Prospecting' && (
                              <Box display='flex' gap={1}>
                                <IconButton
                                  color='success'
                                  onClick={() => handleApprove(c.id)}
                                  disabled={actionLoading[c.id]}
                                  title='Approve Case'
                                >
                                  {actionLoading[c.id] ? (
                                    <CircularProgress
                                      size={20}
                                      color='success'
                                    />
                                  ) : (
                                    <CheckIcon />
                                  )}
                                </IconButton>
                                <IconButton
                                  color='error'
                                  onClick={() => setRejectingId(c.id)}
                                  disabled={actionLoading[c.id]}
                                  title='Reject Case'
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Box>
                            )}
                            {c.status === 'Rejected' && (
                              <IconButton
                                color='success'
                                onClick={() => handleApprove(c.id)}
                                disabled={actionLoading[c.id]}
                                title='Approve Case'
                              >
                                {actionLoading[c.id] ? (
                                  <CircularProgress size={20} color='success' />
                                ) : (
                                  <RefreshIcon />
                                )}
                              </IconButton>
                            )}
                            {c.status === 'Approved' && (
                              <Box display='flex' gap={1}>
                                <IconButton
                                  color='primary'
                                  onClick={() => handleRetainerReceived(c.id)}
                                  disabled={actionLoading[c.id]}
                                  title='Mark Retainer Received'
                                >
                                  {actionLoading[c.id] ? (
                                    <CircularProgress
                                      size={20}
                                      color='primary'
                                    />
                                  ) : (
                                    <CheckIcon />
                                  )}
                                </IconButton>
                              </Box>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reject Dialog */}
      <Dialog open={!!rejectingId} onClose={() => setRejectingId(null)}>
        <DialogTitle>Reject Case</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='Rejection Comment'
            type='text'
            fullWidth
            variant='outlined'
            multiline
            rows={4}
            value={rejectComment}
            onChange={e => setRejectComment(e.target.value)}
            disabled={actionLoading[rejectingId || 0]}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectingId(null)}
            disabled={actionLoading[rejectingId || 0]}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            disabled={!rejectComment.trim() || actionLoading[rejectingId || 0]}
            startIcon={
              actionLoading[rejectingId || 0] ? (
                <CircularProgress size={16} />
              ) : null
            }
          >
            {actionLoading[rejectingId || 0] ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Reason Modal */}
      <Dialog
        open={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
      >
        <DialogTitle>Rejection Reason</DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mt: 1 }}>
            {selectedRejectionComment}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectionModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cases;
